import { seededInt } from './seed';

// ─────────────────────────────────────────────
// TypeScript Interfaces
// ─────────────────────────────────────────────

export type InventoryStatus = 'in_stock' | 'low_stock' | 'out_of_stock';
export type RequisitionStatus =
  | 'pending_approval' |'director_approved' |'released';
export type RequisitionStage =
  | 'consultant_review' |'signed_hardcopy' |'chief_director_approval' |'main_stock_release';
export type Priority = 'urgent' | 'high' | 'normal' | 'low';

export interface KpiCard {
  id: string;
  label: string;
  value: string;
  delta: string;
  deltaPositive: boolean;
  iconName: string;
  iconColor: string;
  alert?: boolean;
  alertLabel?: string;
}

export interface InventoryItem {
  id: string;
  itemCode: string;
  description: string;
  category: string;
  warehouse: string;
  quantity: number;
  unit: string;
  reorderLevel: number;
  status: InventoryStatus;
  lastUpdated: string;
}

export interface Requisition {
  id: string;
  moenId: string;
  contractor: string;
  department: string;
  region: string;
  itemCode: string;
  itemDescription: string;
  quantityRequested: number;
  unit: string;
  warehouse: string;
  stage: RequisitionStage;
  status: RequisitionStatus;
  priority: Priority;
  hardcopyRef: string;
  submittedDate: string;
  approvalDate: string | null;
  releaseDate: string | null;
}

export interface ConnectionCountry {
  id: string;
  country: string;
  program: string;
  utility: string;
  accessPercent: number;
  targetPercent: number;
  households: number;
  newThisMonth: number;
  status: 'electrified' | 'unelectrified' | 'pending_approval';
  tiers: { tier0: number; tier1: number; tier2: number; tier3: number; tier4: number; tier5: number };
  attributes: { capacity: number; duration: number; reliability: number; affordability: number };
  modalTier: number;
}

export interface ConnectionMonthly {
  month: string;
  newConnections: number;
  cumulative: number;
}

// ─────────────────────────────────────────────
// KPI Summary
// ─────────────────────────────────────────────

export const kpiSummary: KpiCard[] = [
  {
    id: 'kpi-001',
    label: 'Total Line Materials',
    value: '4,820',
    delta: '+3.2% vs last quarter',
    deltaPositive: true,
    iconName: 'Package',
    iconColor: '#0D3B2E',
  },
  {
    id: 'kpi-002',
    label: 'Active Requisitions',
    value: '37',
    delta: '-8 from last week',
    deltaPositive: true,
    iconName: 'ClipboardList',
    iconColor: '#C9973A',
  },
  {
    id: 'kpi-003',
    label: 'Pending CD Approvals',
    value: '12',
    delta: 'Urgent — action required',
    deltaPositive: false,
    iconName: 'AlertTriangle',
    iconColor: '#DC2626',
    alert: true,
    alertLabel: 'URGENT',
  },
  {
    id: 'kpi-004',
    label: 'Tracked PEPT Connections',
    value: '298,052',
    delta: '+1,240 this month',
    deltaPositive: true,
    iconName: 'Zap',
    iconColor: '#4A9EB5',
  },
];

// ─────────────────────────────────────────────
// Inventory Items
// ─────────────────────────────────────────────

export const inventoryItems: InventoryItem[] = [
  {
    id: 'inv-001',
    itemCode: 'DT-100KVA',
    description: 'Distribution Transformer 100 KVA',
    category: 'Transformers',
    warehouse: 'Main Warehouse - Tema',
    quantity: 148,
    unit: 'Units',
    reorderLevel: 50,
    status: 'in_stock',
    lastUpdated: '2026-07-22',
  },
  {
    id: 'inv-002',
    itemCode: 'DT-315KVA',
    description: 'Distribution Transformer 315 KVA',
    category: 'Transformers',
    warehouse: 'Main Warehouse - Tema',
    quantity: 42,
    unit: 'Units',
    reorderLevel: 40,
    status: 'low_stock',
    lastUpdated: '2026-07-21',
  },
  {
    id: 'inv-003',
    itemCode: 'LVC-25MM',
    description: 'LV Cable 25mm² Armoured',
    category: 'Cables',
    warehouse: 'Main Warehouse - Tema',
    quantity: 1200,
    unit: 'Metres',
    reorderLevel: 300,
    status: 'in_stock',
    lastUpdated: '2026-07-23',
  },
  {
    id: 'inv-004',
    itemCode: 'LVC-70MM',
    description: 'LV Cable 70mm² Armoured',
    category: 'Cables',
    warehouse: 'Regional Depot - Kumasi',
    quantity: 640,
    unit: 'Metres',
    reorderLevel: 200,
    status: 'in_stock',
    lastUpdated: '2026-07-20',
  },
  {
    id: 'inv-005',
    itemCode: 'PPM-STS',
    description: 'Prepaid Meter STS Single Phase',
    category: 'Meters',
    warehouse: 'Main Warehouse - Tema',
    quantity: 875,
    unit: 'Units',
    reorderLevel: 200,
    status: 'in_stock',
    lastUpdated: '2026-07-23',
  },
  {
    id: 'inv-006',
    itemCode: 'PPM-3PH',
    description: 'Prepaid Meter 3-Phase Commercial',
    category: 'Meters',
    warehouse: 'Regional Depot - Kumasi',
    quantity: 88,
    unit: 'Units',
    reorderLevel: 100,
    status: 'low_stock',
    lastUpdated: '2026-07-19',
  },
  {
    id: 'inv-007',
    itemCode: 'ACSR-Dog',
    description: 'ACSR Dog Conductor',
    category: 'Conductors',
    warehouse: 'Field Store - Tamale',
    quantity: 2400,
    unit: 'Metres',
    reorderLevel: 500,
    status: 'in_stock',
    lastUpdated: '2026-07-18',
  },
  {
    id: 'inv-008',
    itemCode: 'ACSR-Rabbit',
    description: 'ACSR Rabbit Conductor',
    category: 'Conductors',
    warehouse: 'Field Store - Tamale',
    quantity: 40,
    unit: 'Metres',
    reorderLevel: 300,
    status: 'out_of_stock',
    lastUpdated: '2026-07-15',
  },
  {
    id: 'inv-009',
    itemCode: 'GRD-ROD-16',
    description: 'Grounding Rod 16mm Copper Clad',
    category: 'Grounding',
    warehouse: 'Main Warehouse - Tema',
    quantity: 320,
    unit: 'Units',
    reorderLevel: 100,
    status: 'in_stock',
    lastUpdated: '2026-07-22',
  },
  {
    id: 'inv-010',
    itemCode: 'GRD-ROD-20',
    description: 'Grounding Rod 20mm Copper Clad',
    category: 'Grounding',
    warehouse: 'Regional Depot - Kumasi',
    quantity: 65,
    unit: 'Units',
    reorderLevel: 80,
    status: 'low_stock',
    lastUpdated: '2026-07-17',
  },
  {
    id: 'inv-011',
    itemCode: 'SVC-DROP',
    description: 'Service Drop Cable 16mm²',
    category: 'Cables',
    warehouse: 'Field Store - Tamale',
    quantity: 0,
    unit: 'Metres',
    reorderLevel: 400,
    status: 'out_of_stock',
    lastUpdated: '2026-07-10',
  },
  {
    id: 'inv-012',
    itemCode: 'DT-500KVA',
    description: 'Distribution Transformer 500 KVA',
    category: 'Transformers',
    warehouse: 'Main Warehouse - Tema',
    quantity: 18,
    unit: 'Units',
    reorderLevel: 15,
    status: 'in_stock',
    lastUpdated: '2026-07-23',
  },
];

// ─────────────────────────────────────────────
// Requisitions
// ─────────────────────────────────────────────

export const requisitions: Requisition[] = [
  {
    id: 'req-001',
    moenId: 'MOEN-REQ-0041',
    contractor: 'Volta River Authority',
    department: 'Greater Accra Region',
    region: 'Greater Accra',
    itemCode: 'DT-100KVA',
    itemDescription: 'Distribution Transformer 100 KVA',
    quantityRequested: 12,
    unit: 'Units',
    warehouse: 'Main Warehouse - Tema',
    stage: 'main_stock_release',
    status: 'released',
    priority: 'normal',
    hardcopyRef: 'HC-2026-0041-A',
    submittedDate: '2026-07-10',
    approvalDate: '2026-07-14',
    releaseDate: '2026-07-18',
  },
  {
    id: 'req-009',
    moenId: 'MOEN-REQ-0030',
    contractor: 'Volta River Authority',
    department: 'Greater Accra Region',
    region: 'Greater Accra',
    itemCode: 'PPM-STS',
    itemDescription: 'Prepaid Meter STS Single Phase',
    quantityRequested: 250,
    unit: 'Units',
    warehouse: 'Main Warehouse - Tema',
    stage: 'main_stock_release',
    status: 'released',
    priority: 'high',
    hardcopyRef: 'HC-2026-0030-A',
    submittedDate: '2026-01-10',
    approvalDate: '2026-01-14',
    releaseDate: '2026-01-20',
  },
  {
    id: 'req-010',
    moenId: 'MOEN-REQ-0031',
    contractor: 'Volta River Authority',
    department: 'Greater Accra Region',
    region: 'Greater Accra',
    itemCode: 'DT-100KVA',
    itemDescription: 'Distribution Transformer 100 KVA',
    quantityRequested: 8,
    unit: 'Units',
    warehouse: 'Main Warehouse - Tema',
    stage: 'main_stock_release',
    status: 'released',
    priority: 'normal',
    hardcopyRef: 'HC-2026-0031-A',
    submittedDate: '2026-02-05',
    approvalDate: '2026-02-09',
    releaseDate: '2026-02-15',
  },
  {
    id: 'req-011',
    moenId: 'MOEN-REQ-0032',
    contractor: 'Kumasi Metropolitan Assembly',
    department: 'Ashanti Region',
    region: 'Ashanti',
    itemCode: 'DT-315KVA',
    itemDescription: 'Distribution Transformer 315 KVA',
    quantityRequested: 3,
    unit: 'Units',
    warehouse: 'Regional Depot - Kumasi',
    stage: 'main_stock_release',
    status: 'released',
    priority: 'high',
    hardcopyRef: 'HC-2026-0032-B',
    submittedDate: '2026-01-15',
    approvalDate: '2026-01-20',
    releaseDate: '2026-01-28',
  },
  {
    id: 'req-012',
    moenId: 'MOEN-REQ-0033',
    contractor: 'Kumasi Metropolitan Assembly',
    department: 'Ashanti Region',
    region: 'Ashanti',
    itemCode: 'PPM-STS',
    itemDescription: 'Prepaid Meter STS Single Phase',
    quantityRequested: 180,
    unit: 'Units',
    warehouse: 'Main Warehouse - Tema',
    stage: 'main_stock_release',
    status: 'released',
    priority: 'normal',
    hardcopyRef: 'HC-2026-0033-A',
    submittedDate: '2026-03-01',
    approvalDate: '2026-03-05',
    releaseDate: '2026-03-12',
  },
  {
    id: 'req-013',
    moenId: 'MOEN-REQ-0034',
    contractor: 'Northern Electricity Distribution Co.',
    department: 'Northern Region',
    region: 'Northern',
    itemCode: 'DT-100KVA',
    itemDescription: 'Distribution Transformer 100 KVA',
    quantityRequested: 6,
    unit: 'Units',
    warehouse: 'Field Store - Tamale',
    stage: 'main_stock_release',
    status: 'released',
    priority: 'urgent',
    hardcopyRef: 'HC-2026-0034-A',
    submittedDate: '2025-11-10',
    approvalDate: '2025-11-15',
    releaseDate: '2025-11-22',
  },
  {
    id: 'req-014',
    moenId: 'MOEN-REQ-0035',
    contractor: 'ECG - Eastern Region',
    department: 'Eastern Region',
    region: 'Eastern',
    itemCode: 'PPM-STS',
    itemDescription: 'Prepaid Meter STS Single Phase',
    quantityRequested: 320,
    unit: 'Units',
    warehouse: 'Main Warehouse - Tema',
    stage: 'main_stock_release',
    status: 'released',
    priority: 'high',
    hardcopyRef: 'HC-2026-0035-C',
    submittedDate: '2025-12-01',
    approvalDate: '2025-12-06',
    releaseDate: '2025-12-14',
  },
  {
    id: 'req-015',
    moenId: 'MOEN-REQ-0036',
    contractor: 'ECG - Eastern Region',
    department: 'Eastern Region',
    region: 'Eastern',
    itemCode: 'DT-100KVA',
    itemDescription: 'Distribution Transformer 100 KVA',
    quantityRequested: 10,
    unit: 'Units',
    warehouse: 'Main Warehouse - Tema',
    stage: 'main_stock_release',
    status: 'released',
    priority: 'normal',
    hardcopyRef: 'HC-2026-0036-A',
    submittedDate: '2026-02-10',
    approvalDate: '2026-02-14',
    releaseDate: '2026-02-20',
  },
  {
    id: 'req-016',
    moenId: 'MOEN-REQ-0037',
    contractor: 'Western Region Electrification Proj.',
    department: 'Western Region',
    region: 'Western',
    itemCode: 'PPM-STS',
    itemDescription: 'Prepaid Meter STS Single Phase',
    quantityRequested: 200,
    unit: 'Units',
    warehouse: 'Main Warehouse - Tema',
    stage: 'main_stock_release',
    status: 'released',
    priority: 'high',
    hardcopyRef: 'HC-2026-0037-A',
    submittedDate: '2025-10-05',
    approvalDate: '2025-10-10',
    releaseDate: '2025-10-18',
  },
  {
    id: 'req-002',
    moenId: 'MOEN-REQ-0042',
    contractor: 'Kumasi Metropolitan Assembly',
    department: 'Ashanti Region',
    region: 'Ashanti',
    itemCode: 'LVC-25MM',
    itemDescription: 'LV Cable 25mm² Armoured',
    quantityRequested: 500,
    unit: 'Metres',
    warehouse: 'Regional Depot - Kumasi',
    stage: 'chief_director_approval',
    status: 'pending_approval',
    priority: 'urgent',
    hardcopyRef: 'HC-2026-0042-B',
    submittedDate: '2026-07-16',
    approvalDate: null,
    releaseDate: null,
  },
  {
    id: 'req-003',
    moenId: 'MOEN-REQ-0043',
    contractor: 'Northern Electricity Distribution Co.',
    department: 'Northern Region',
    region: 'Northern',
    itemCode: 'ACSR-Dog',
    itemDescription: 'ACSR Dog Conductor',
    quantityRequested: 800,
    unit: 'Metres',
    warehouse: 'Field Store - Tamale',
    stage: 'signed_hardcopy',
    status: 'pending_approval',
    priority: 'high',
    hardcopyRef: 'HC-2026-0043-A',
    submittedDate: '2026-07-18',
    approvalDate: null,
    releaseDate: null,
  },
  {
    id: 'req-004',
    moenId: 'MOEN-REQ-0044',
    contractor: 'ECG - Eastern Region',
    department: 'Eastern Region',
    region: 'Eastern',
    itemCode: 'PPM-STS',
    itemDescription: 'Prepaid Meter STS Single Phase',
    quantityRequested: 200,
    unit: 'Units',
    warehouse: 'Main Warehouse - Tema',
    stage: 'chief_director_approval',
    status: 'pending_approval',
    priority: 'urgent',
    hardcopyRef: 'HC-2026-0044-C',
    submittedDate: '2026-07-19',
    approvalDate: null,
    releaseDate: null,
  },
  {
    id: 'req-005',
    moenId: 'MOEN-REQ-0045',
    contractor: 'Savannah Accelerated Dev. Auth.',
    department: 'Savannah Region',
    region: 'Northern',
    itemCode: 'GRD-ROD-16',
    itemDescription: 'Grounding Rod 16mm Copper Clad',
    quantityRequested: 150,
    unit: 'Units',
    warehouse: 'Main Warehouse - Tema',
    stage: 'consultant_review',
    status: 'pending_approval',
    priority: 'normal',
    hardcopyRef: 'HC-2026-0045-A',
    submittedDate: '2026-07-21',
    approvalDate: null,
    releaseDate: null,
  },
  {
    id: 'req-006',
    moenId: 'MOEN-REQ-0038',
    contractor: 'Brong-Ahafo Regional Works',
    department: 'Bono Region',
    region: 'Ashanti',
    itemCode: 'LVC-70MM',
    itemDescription: 'LV Cable 70mm² Armoured',
    quantityRequested: 300,
    unit: 'Metres',
    warehouse: 'Regional Depot - Kumasi',
    stage: 'main_stock_release',
    status: 'director_approved',
    priority: 'high',
    hardcopyRef: 'HC-2026-0038-B',
    submittedDate: '2026-07-05',
    approvalDate: '2026-07-09',
    releaseDate: null,
  },
  {
    id: 'req-007',
    moenId: 'MOEN-REQ-0039',
    contractor: 'Tamale City Infrastructure Unit',
    department: 'Northern Region',
    region: 'Northern',
    itemCode: 'DT-315KVA',
    itemDescription: 'Distribution Transformer 315 KVA',
    quantityRequested: 4,
    unit: 'Units',
    warehouse: 'Main Warehouse - Tema',
    stage: 'chief_director_approval',
    status: 'pending_approval',
    priority: 'urgent',
    hardcopyRef: 'HC-2026-0039-A',
    submittedDate: '2026-07-08',
    approvalDate: null,
    releaseDate: null,
  },
  {
    id: 'req-008',
    moenId: 'MOEN-REQ-0040',
    contractor: 'Western Region Electrification Proj.',
    department: 'Western Region',
    region: 'Western',
    itemCode: 'PPM-3PH',
    itemDescription: 'Prepaid Meter 3-Phase Commercial',
    quantityRequested: 60,
    unit: 'Units',
    warehouse: 'Regional Depot - Kumasi',
    stage: 'signed_hardcopy',
    status: 'pending_approval',
    priority: 'normal',
    hardcopyRef: 'HC-2026-0040-B',
    submittedDate: '2026-07-12',
    approvalDate: null,
    releaseDate: null,
  },
];

// ─────────────────────────────────────────────
// Tier computation helpers (module-level, not render-time)
// ─────────────────────────────────────────────

function computeTiers(id: string, accessPercent: number): {
  tiers: { tier0: number; tier1: number; tier2: number; tier3: number; tier4: number; tier5: number };
  attributes: { capacity: number; duration: number; reliability: number; affordability: number };
  modalTier: number;
} {
  const tier0 = 100 - accessPercent;
  const remaining = accessPercent;

  const raw = [1, 2, 3, 4, 5].map(i => seededInt(id + 'tier' + i, 10, 100));
  const decay = [1, 0.85, 0.72, 0.58, 0.46];
  const weighted = raw.map((r, idx) => r * decay[idx]);
  const total = weighted.reduce((a, b) => a + b, 0);
  const fractions = weighted.map(w => w / total);

  const tier1 = Math.round(remaining * fractions[0]);
  const tier2 = Math.round(remaining * fractions[1]);
  const tier3 = Math.round(remaining * fractions[2]);
  const tier4 = Math.round(remaining * fractions[3]);
  const tier5 = remaining - tier1 - tier2 - tier3 - tier4;

  const connectedTierSum = tier1 + tier2 + tier3 + tier4 + tier5;
  const weightedAvgTier = connectedTierSum === 0 ? 0 :
    (1 * tier1 + 2 * tier2 + 3 * tier3 + 4 * tier4 + 5 * tier5) / connectedTierSum;

  function attrScore(attr: string): number {
    const jitter = (seededInt(id + attr, 0, 12) - 6) / 10;
    const score = Math.max(0, Math.min(5, weightedAvgTier + jitter));
    return Math.round(score * 10) / 10;
  }

  const modalTier = Math.round(weightedAvgTier);

  return {
    tiers: { tier0, tier1, tier2, tier3, tier4, tier5 },
    attributes: {
      capacity: attrScore('capacity'),
      duration: attrScore('duration'),
      reliability: attrScore('reliability'),
      affordability: attrScore('affordability'),
    },
    modalTier,
  };
}

// Pre-compute tier data for each country record
// cc-001: Cote d'Ivoire, accessPercent=68
const _cc001 = computeTiers('cc-001', 68);
// cc-002: Senegal, accessPercent=76
const _cc002 = computeTiers('cc-002', 76);
// cc-003: Togo, accessPercent=46
const _cc003 = computeTiers('cc-003', 46);
// cc-004: Benin, accessPercent=41
const _cc004 = computeTiers('cc-004', 41);
// cc-005: Ghana, accessPercent=84
const _cc005 = computeTiers('cc-005', 84);

// ─────────────────────────────────────────────
// Connections by Country
// ─────────────────────────────────────────────

export const connectionsByCountry: ConnectionCountry[] = [
  {
    id: 'cc-001',
    country: "Cote d'Ivoire",
    program: 'PEPT',
    utility: 'CIE / ANARE',
    accessPercent: 68,
    targetPercent: 100,
    households: 52340,
    newThisMonth: 284,
    status: 'pending_approval',
    tiers: _cc001.tiers,
    attributes: _cc001.attributes,
    modalTier: _cc001.modalTier,
  },
  {
    id: 'cc-002',
    country: 'Senegal',
    program: 'SE4ALL',
    utility: 'SENELEC',
    accessPercent: 76,
    targetPercent: 100,
    households: 71820,
    newThisMonth: 312,
    status: 'pending_approval',
    tiers: _cc002.tiers,
    attributes: _cc002.attributes,
    modalTier: _cc002.modalTier,
  },
  {
    id: 'cc-003',
    country: 'Togo',
    program: 'CEET',
    utility: 'CEET',
    accessPercent: 46,
    targetPercent: 100,
    households: 28910,
    newThisMonth: 186,
    status: 'unelectrified',
    tiers: _cc003.tiers,
    attributes: _cc003.attributes,
    modalTier: _cc003.modalTier,
  },
  {
    id: 'cc-004',
    country: 'Benin',
    program: 'DGRE',
    utility: 'SBEE',
    accessPercent: 41,
    targetPercent: 100,
    households: 23640,
    newThisMonth: 142,
    status: 'unelectrified',
    tiers: _cc004.tiers,
    attributes: _cc004.attributes,
    modalTier: _cc004.modalTier,
  },
  {
    id: 'cc-005',
    country: 'Ghana',
    program: 'PEPT / NEP',
    utility: 'ECG / NEDCO',
    accessPercent: 84,
    targetPercent: 100,
    households: 121342,
    newThisMonth: 316,
    status: 'electrified',
    tiers: _cc005.tiers,
    attributes: _cc005.attributes,
    modalTier: _cc005.modalTier,
  },
];

// ─────────────────────────────────────────────
// Ghana Regions (for reconciliation)
// ─────────────────────────────────────────────

export const ghanaRegions = [
  'Greater Accra',
  'Ashanti',
  'Northern',
  'Eastern',
  'Western',
];

// ─────────────────────────────────────────────
// Monthly Connection Tracking
// ─────────────────────────────────────────────

export const connectionTracking: ConnectionMonthly[] = [
  { month: 'Aug 25', newConnections: 980, cumulative: 285120 },
  { month: 'Sep 25', newConnections: 1140, cumulative: 286260 },
  { month: 'Oct 25', newConnections: 870, cumulative: 287130 },
  { month: 'Nov 25', newConnections: 1320, cumulative: 288450 },
  { month: 'Dec 25', newConnections: 760, cumulative: 289210 },
  { month: 'Jan 26', newConnections: 1050, cumulative: 290260 },
  { month: 'Feb 26', newConnections: 1180, cumulative: 291440 },
  { month: 'Mar 26', newConnections: 940, cumulative: 292380 },
  { month: 'Apr 26', newConnections: 1410, cumulative: 293790 },
  { month: 'May 26', newConnections: 1080, cumulative: 294870 },
  { month: 'Jun 26', newConnections: 1702, cumulative: 296572 },
  { month: 'Jul 26', newConnections: 1240, cumulative: 298052 },
];

// ─────────────────────────────────────────────
// Regional Connection Observations (for reconciliation)
// ─────────────────────────────────────────────

export interface RegionalConnection {
  region: string;
  month: string;
  monthIndex: number;
  reportedConnections: number;
  verifiedConnections: number;
}

function buildConnectionsByRegion(): RegionalConnection[] {
  const records: RegionalConnection[] = [];
  for (const region of ghanaRegions) {
    for (let mi = 0; mi < connectionTracking.length; mi++) {
      const month = connectionTracking[mi].month;
      let reported = seededInt(region + month, 120, 900);
      const verifiedPct = seededInt(region + month + 'v', 62, 96);
      let verified = Math.round(reported * (verifiedPct / 100));

      // Depress Northern region from monthIndex 4 onwards
      if (region === 'Northern' && mi >= 4) {
        reported = Math.round(reported * 0.45);
        verified = Math.round(verified * 0.45);
      }
      // Depress Western region from monthIndex 4 onwards
      if (region === 'Western' && mi >= 4) {
        reported = Math.round(reported * 0.62);
        verified = Math.round(verified * 0.62);
      }

      records.push({
        region,
        month,
        monthIndex: mi,
        reportedConnections: reported,
        verifiedConnections: verified,
      });
    }
  }
  return records;
}

export const connectionsByRegion: RegionalConnection[] = buildConnectionsByRegion();